/* Ocean Hero v3 — dependency-free WebGL scene for the AllShipModels homepage.
   Dusk sea with sharp-crested swell, sub-surface glow, whitecaps, drifting
   clouds and a model ship riding the waves.
   Interaction: the camera follows the mouse on a smooth spring, the ship leans
   toward the cursor, moving over the water leaves a gentle wake, and a click
   sends out a ripple.
   Performance: a fixed pixel budget (not raw devicePixelRatio), cheap per-vertex
   swell + tiny per-pixel detail, adaptive resolution, 60fps cap on 120Hz
   screens, and full stop when off-screen or the tab is hidden. Reduced-motion
   users get a calm version; no WebGL gets a static fallback. */
(function () {
  'use strict';

  var PREC = [
    '#ifdef GL_FRAGMENT_PRECISION_HIGH',
    'precision highp float;',
    '#else',
    'precision mediump float;',
    '#endif',
  ].join('\n');

  // ---------- shared GLSL ----------
  var SKY_FN = [
    'uniform vec3 uSunDir;',
    'uniform vec3 uSkyTop; uniform vec3 uSkyMid; uniform vec3 uHorizon; uniform vec3 uSunCol;',
    'float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }',
    'vec3 skyBase(vec3 d){',
    '  float h = max(d.y, 0.0);',
    '  vec3 c = mix(uHorizon, uSkyMid, smoothstep(0.0, 0.13, h));',
    '  c = mix(c, uSkyTop, smoothstep(0.1, 0.6, h));',
    '  float s = max(dot(d, uSunDir), 0.0);',
    '  c += uSunCol * (pow(s, 6.0) * 0.30 + pow(s, 64.0) * 0.55);',
    '  c += uSunCol * smoothstep(0.99955, 0.9998, s) * 1.2;',
    '  return c;',
    '}',
  ].join('\n');

  // Swell: sharp-crested "exp-sine" waves. Mirrored on the CPU in waveAt().
  var SWELL = [
    'uniform float uTime;',
    'uniform vec4 uRip[8];', // x,z = origin, z = start time, w = amplitude (0 = off)
    'uniform vec4 uRogue;', // xy = travel dir, z = crest position along dir, w = height (0 = none)
    // Rogue swell: a single broad crest rolling toward the viewer, steeper on its
    // leading face and tapering at the sides. One extra term per vertex.
    'void rogue(vec2 p, inout float h, inout vec2 g){',
    '  if (uRogue.w <= 0.0) return;',
    '  vec2 d = uRogue.xy; vec2 pd = vec2(-d.y, d.x);',
    '  float u = dot(p, d) - uRogue.z;',
    '  float kk = u > 0.0 ? 0.42 : 0.24;',
    '  float perp = dot(p, pd);',
    '  float side = exp(-perp * perp * 0.0022);',
    '  float x = clamp(u * kk, -12.0, 12.0); float ex = exp(x), emx = exp(-x);',
    '  float ch = 2.0 / (ex + emx); float s2 = ch * ch; float th = (ex - emx) / (ex + emx);',
    '  float a = uRogue.w * side;',
    '  h += a * s2;',
    '  g += a * (-2.0 * s2 * th * kk) * d + uRogue.w * s2 * side * (-2.0 * perp * 0.0022) * pd;',
    '}',
    'void swellWave(vec2 p, vec2 d, float a, float k, float s, inout float h, inout vec2 g){',
    '  float ph = dot(d, p) * k + uTime * s;',
    '  float e = exp(sin(ph) - 1.0);',
    '  h += a * (e - 0.45); g += a * e * cos(ph) * k * d;',
    '}',
    'void ripples(vec2 p, inout float h, inout vec2 g){',
    '  for (int i = 0; i < 8; i++) {',
    '    vec4 r = uRip[i];',
    '    if (r.w > 0.0) {',
    '      float age = uTime - r.z;',
    '      vec2 dv = p - r.xy; float rr = length(dv) + 1e-3;',
    '      float x = rr - age * 2.6;',
    '      float env = exp(-x * x * 0.35) * exp(-age * 0.45) * r.w / (1.0 + rr * 0.06) * (1.0 - exp(-rr * rr * 0.5));',
    '      float ph = x * 2.4;',
    '      h += env * sin(ph);',
    '      g += env * 2.4 * cos(ph) * (dv / rr);',
    '    }',
    '  }',
    '}',
    'float swell(vec2 p, out vec2 g){',
    '  float h = 0.0; g = vec2(0.0);',
    '  swellWave(p, vec2(0.944, 0.330), 0.20, 0.36, 0.62, h, g);',
    '  swellWave(p, vec2(-0.514, 0.857), 0.13, 0.58, 0.85, h, g);',
    '  swellWave(p, vec2(0.196, -0.981), 0.07, 1.05, 1.15, h, g);',
    '  swellWave(p, vec2(0.747, 0.664), 0.045, 1.70, 1.55, h, g);',
    '  swellWave(p, vec2(-0.894, -0.447), 0.025, 2.60, 2.00, h, g);',
    '  return h;',
    '}',
  ].join('\n');

  var VS_SKY = [
    'attribute vec2 aPos;',
    'varying vec2 vUv;',
    'void main(){ vUv = aPos; gl_Position = vec4(aPos, 0.9999, 1.0); }',
  ].join('\n');

  var FS_SKY = [
    PREC,
    'varying vec2 vUv;',
    'uniform mat4 uInvViewProj; uniform vec3 uCamPos; uniform float uTime;',
    SKY_FN,
    'float vnoise(vec2 p){',
    '  vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);',
    '  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + 1.0), f.x), f.y);',
    '}',
    'void main(){',
    '  vec4 p = uInvViewProj * vec4(vUv, 1.0, 1.0);',
    '  vec3 d = normalize(p.xyz / p.w - uCamPos);',
    '  vec3 c = skyBase(d);',
    '  float h = max(d.y, 0.0);',
    '#ifndef LITE',
    // stars
    '  vec2 q = vec2(atan(d.x, d.z), d.y) * 620.0;',
    '  vec2 gq = floor(q); vec2 fq = fract(q) - 0.5;',
    '  float st = step(0.9983, hash(gq)) * (1.0 - smoothstep(0.08, 0.32, length(fq)));',
    '  float tw = 0.6 + 0.4 * sin(uTime * (1.5 + hash(gq + 3.0) * 3.0) + hash(gq) * 30.0);',
    '  c += vec3(st * tw) * (0.35 + 0.45 * hash(gq + 7.0)) * smoothstep(0.16, 0.55, h);',
    '#endif',
    // drifting cloud bank, lit from below by the sun
    '  vec2 cp = d.xz / (d.y + 0.07) * 0.9 + vec2(uTime * 0.012, 0.0);',
    '  float n = vnoise(cp) * 0.55 + vnoise(cp * 2.1 + 3.7) * 0.3;',
    '#ifndef LITE',
    '  n += vnoise(cp * 4.3 + 9.1) * 0.15;',
    '#endif',
    '  float band = smoothstep(0.012, 0.07, h) * (1.0 - smoothstep(0.22, 0.5, h));',
    '  float ca = smoothstep(0.5, 0.78, n) * band * 0.85;',
    '  float s = max(dot(d, uSunDir), 0.0);',
    '  vec3 cc = mix(uSkyMid * 0.55, uSunCol * 1.05, pow(s, 3.0) * 0.9 + (1.0 - smoothstep(0.02, 0.16, h)) * 0.35);',
    '  c = mix(c, cc, ca);',
    '  gl_FragColor = vec4(c, 1.0);',
    '}',
  ].join('\n');

  var VS_SEA = [
    'attribute vec2 aGrid;',
    'uniform mat4 uViewProj;',
    'varying vec3 vWorld; varying vec2 vGrad; varying float vRogue;',
    SWELL,
    'void main(){',
    '  vec2 g; float h = swell(aGrid, g);',
    '  float h0 = h; rogue(aGrid, h, g); vRogue = h - h0;',
    '  ripples(aGrid, h, g);',
    '  vWorld = vec3(aGrid.x, h, aGrid.y); vGrad = g;',
    '  gl_Position = uViewProj * vec4(vWorld, 1.0);',
    '}',
  ].join('\n');

  var FS_SEA = [
    PREC,
    'varying vec3 vWorld; varying vec2 vGrad; varying float vRogue;',
    'uniform vec3 uCamPos; uniform vec3 uDeep; uniform vec3 uSSS; uniform float uFogDist;',
    'uniform vec3 uCursor;', // xz = cursor on water, y = strength
    SKY_FN,
    SWELL,
    // small capillary detail, gradient only (cheap)
    'vec2 detail(vec2 p){',
    '  vec2 g = vec2(0.0); float ph;',
    '  ph = dot(p, vec2(0.60, 0.80)) * 3.7 + uTime * 2.6;  g += 0.020 * 3.7 * cos(ph) * vec2(0.60, 0.80);',
    '  ph = dot(p, vec2(-0.85, 0.53)) * 5.9 + uTime * 3.3; g += 0.012 * 5.9 * cos(ph) * vec2(-0.85, 0.53);',
    '#ifndef LITE',
    '  ph = dot(p, vec2(0.28, -0.96)) * 9.3 + uTime * 4.1; g += 0.006 * 9.3 * cos(ph) * vec2(0.28, -0.96);',
    '#endif',
    '  return g;',
    '}',
    'void main(){',
    '  float dist = length(uCamPos - vWorld);',
    '  float near = 1.0 - smoothstep(6.0, 60.0, dist);',
    '  vec2 g = vGrad + detail(vWorld.xz) * near;',
    '  float hh = 0.0; vec2 rg = vec2(0.0); ripples(vWorld.xz, hh, rg); g += rg * 0.8;',
    '  vec3 n = normalize(vec3(-g.x * 0.85, 1.0, -g.y * 0.85));',
    '  n = normalize(mix(n, vec3(0.0, 1.0, 0.0), smoothstep(12.0, uFogDist, dist) * 0.75));',
    '  vec3 v = normalize(uCamPos - vWorld);',
    '  vec3 r = reflect(-v, n); r.y = abs(r.y);',
    '  float fres = 0.02 + 0.98 * pow(1.0 - max(dot(n, v), 0.0), 5.0);',
    '  vec3 refl = skyBase(r);',
    '  float sy = vWorld.y - vRogue * 0.8;', // swell-only height, so the rogue does not flood the whole face
    '  float crest = smoothstep(0.05, 0.32, sy);',
    // sub-surface glow: light passing through wave crests backlit by the sun
    '  float sss = crest * (0.35 + 0.65 * pow(max(dot(normalize(vec3(-v.x, 0.0, -v.z)), vec3(uSunDir.x, 0.0, uSunDir.z)), 0.0), 3.0));',
    '  float facing = max(dot(n, v), 0.0);',
    '  sss *= 1.0 - smoothstep(0.25, 0.7, facing);', // glow shows through thin crests seen edge-on, not broad faces
    '  vec3 body = uDeep * (0.7 + 0.45 * crest) * (1.0 - 0.35 * smoothstep(0.3, 0.8, facing)) + uSSS * sss * 0.55;',
    '  vec3 c = mix(body, refl, fres);',
    // sun glitter: sharp core + broad path
    '  float sd = max(dot(r, uSunDir), 0.0);',
    '  c += uSunCol * (pow(sd, 900.0) * 1.8 + pow(sd, 90.0) * 0.14);',
    // whitecaps on the highest crests, broken up so they read as foam
    '  float foamN = sin(vWorld.x * 3.1 + uTime * 0.7) * sin(vWorld.z * 2.7 - uTime * 0.5) * 0.5 + 0.5;',
    '  float foam = smoothstep(0.30, 0.46, sy + foamN * 0.08) * near;',
    '  vec2 rd = uRogue.xy; float ru = dot(vWorld.xz, rd) - uRogue.z; float rp = dot(vWorld.xz, vec2(-rd.y, rd.x));',
    '  float streak = sin(vWorld.x * 1.7 + vWorld.z * 0.6 + uTime * 0.9) * sin(vWorld.x * 4.3 - uTime * 1.3) * 0.5 + 0.5;',
    '  float band = smoothstep(-1.4, 0.0, ru) * (1.0 - smoothstep(0.4, 2.0, ru)) * exp(-rp * rp * 0.003);',
    '  float rf = band * smoothstep(0.3, 0.7, streak * 0.6 + foamN * 0.4) * smoothstep(0.35, 0.9, uRogue.w)',
    '           * smoothstep(9.0, 20.0, dist) * (1.0 - smoothstep(70.0, 160.0, dist));',
    '  foam = max(foam, rf);',
    '  foam = max(foam, smoothstep(0.05, 0.16, hh + (foamN - 0.5) * 0.04) * 0.6 * (1.0 - smoothstep(45.0, 100.0, dist)));',
    '  c = mix(c, vec3(0.86, 0.80, 0.74) * (0.55 + 0.45 * fres) + uSunCol * 0.08, foam * 0.55);',
    // soft warm pool of light that follows the cursor across the water
    '  vec2 cd = vWorld.xz - uCursor.xz;',
    '  float pool = exp(-dot(cd, cd) * 0.09) * uCursor.y;',
    '  c += uSunCol * pool * (0.07 + 0.5 * pow(sd, 24.0));',
    '  float fog = smoothstep(uFogDist * 0.22, uFogDist, dist);',
    '  c = mix(c, skyBase(normalize(vec3(-v.x, 0.015, -v.z))), fog);',
    '  gl_FragColor = vec4(c, 1.0);',
    '}',
  ].join('\n');

  var VS_SHIP = [
    'attribute vec2 aPos;',
    'uniform mat4 uViewProj; uniform vec3 uShipPos; uniform vec2 uSize;',
    'uniform float uRoll; uniform float uPitch; uniform float uMirror; uniform float uWater;',
    'varying vec2 vUv; varying float vWorldY;',
    'void main(){',
    '  vUv = vec2(aPos.x, 1.0 - aPos.y);',
    '  vec2 local = vec2((aPos.x - 0.5) * uSize.x, (aPos.y - uWater) * uSize.y);',
    '  if (uMirror > 0.5) local.y = -local.y;',
    '  float cr = cos(uRoll), sr = sin(uRoll);',
    '  vec2 rl = vec2(local.x * cr - local.y * sr, local.x * sr + local.y * cr);',
    '  vec3 w = uShipPos + vec3(rl.x, rl.y, rl.x * sin(uPitch) * 0.25);',
    '  vWorldY = w.y;',
    '  gl_Position = uViewProj * vec4(w, 1.0);',
    '}',
  ].join('\n');

  var FS_SHIP = [
    PREC,
    'varying vec2 vUv; varying float vWorldY;',
    'uniform sampler2D uTex; uniform float uMirror; uniform float uTime; uniform vec3 uTint; uniform float uSeaY; uniform vec3 uSunCol; uniform float uRim; uniform float uRefl;',
    'void main(){',
    '  vec2 uv = vUv;',
    '  if (uMirror > 0.5) { uv.x += sin(uv.y * 55.0 + uTime * 2.6) * 0.005; }',
    '  vec4 t = texture2D(uTex, uv);',
    '  if (t.a < 0.04) discard;',
    '  vec3 c = t.rgb;',
    '  float lum = dot(c, vec3(0.299, 0.587, 0.114));',
    '  c = mix(c, vec3(lum), 0.2) * uTint;',
    // warm dusk light on sails and brightwork
    '  c += uSunCol * smoothstep(0.5, 1.0, lum) * 0.16;',
    // rim light on the sun side (right edge of the sprite)
    '  c += uSunCol * uRim * smoothstep(0.55, 1.0, vUv.x) * lum * 0.25;',
    '  float a = t.a;',
    '  if (uMirror > 0.5) {',
    '    if (vWorldY > uSeaY) discard;',
    '    a *= 0.22 * uRefl * smoothstep(uSeaY - 2.2, uSeaY, vWorldY);',
    '    c *= 0.65;',
    '  }',
    '  gl_FragColor = vec4(c, a);',
    '}',
  ].join('\n');

  // ---------- math helpers ----------
  function perspective(fovy, aspect, near, far) {
    var f = 1 / Math.tan(fovy / 2), nf = 1 / (near - far);
    return [f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, (far + near) * nf, -1, 0, 0, 2 * far * near * nf, 0];
  }
  function lookAt(e, c, u) {
    var zx = e[0] - c[0], zy = e[1] - c[1], zz = e[2] - c[2];
    var l = Math.hypot(zx, zy, zz); zx /= l; zy /= l; zz /= l;
    var xx = u[1] * zz - u[2] * zy, xy = u[2] * zx - u[0] * zz, xz = u[0] * zy - u[1] * zx;
    l = Math.hypot(xx, xy, xz); xx /= l; xy /= l; xz /= l;
    var yx = zy * xz - zz * xy, yy = zz * xx - zx * xz, yz = zx * xy - zy * xx;
    return [xx, yx, zx, 0, xy, yy, zy, 0, xz, yz, zz, 0,
      -(xx * e[0] + xy * e[1] + xz * e[2]), -(yx * e[0] + yy * e[1] + yz * e[2]), -(zx * e[0] + zy * e[1] + zz * e[2]), 1];
  }
  function mul(a, b) {
    var o = new Float32Array(16);
    for (var i = 0; i < 4; i++) for (var j = 0; j < 4; j++) {
      o[j * 4 + i] = a[i] * b[j * 4] + a[4 + i] * b[j * 4 + 1] + a[8 + i] * b[j * 4 + 2] + a[12 + i] * b[j * 4 + 3];
    }
    return o;
  }
  function invert(m) {
    var inv = new Float32Array(16);
    inv[0] = m[5] * m[10] * m[15] - m[5] * m[11] * m[14] - m[9] * m[6] * m[15] + m[9] * m[7] * m[14] + m[13] * m[6] * m[11] - m[13] * m[7] * m[10];
    inv[4] = -m[4] * m[10] * m[15] + m[4] * m[11] * m[14] + m[8] * m[6] * m[15] - m[8] * m[7] * m[14] - m[12] * m[6] * m[11] + m[12] * m[7] * m[10];
    inv[8] = m[4] * m[9] * m[15] - m[4] * m[11] * m[13] - m[8] * m[5] * m[15] + m[8] * m[7] * m[13] + m[12] * m[5] * m[11] - m[12] * m[7] * m[9];
    inv[12] = -m[4] * m[9] * m[14] + m[4] * m[10] * m[13] + m[8] * m[5] * m[14] - m[8] * m[6] * m[13] - m[12] * m[5] * m[10] + m[12] * m[6] * m[9];
    inv[1] = -m[1] * m[10] * m[15] + m[1] * m[11] * m[14] + m[9] * m[2] * m[15] - m[9] * m[3] * m[14] - m[13] * m[2] * m[11] + m[13] * m[3] * m[10];
    inv[5] = m[0] * m[10] * m[15] - m[0] * m[11] * m[14] - m[8] * m[2] * m[15] + m[8] * m[3] * m[14] + m[12] * m[2] * m[11] - m[12] * m[3] * m[10];
    inv[9] = -m[0] * m[9] * m[15] + m[0] * m[11] * m[13] + m[8] * m[1] * m[15] - m[8] * m[3] * m[13] - m[12] * m[1] * m[11] + m[12] * m[3] * m[9];
    inv[13] = m[0] * m[9] * m[14] - m[0] * m[10] * m[13] - m[8] * m[1] * m[14] + m[8] * m[2] * m[13] + m[12] * m[1] * m[10] - m[12] * m[2] * m[9];
    inv[2] = m[1] * m[6] * m[15] - m[1] * m[7] * m[14] - m[5] * m[2] * m[15] + m[5] * m[3] * m[14] + m[13] * m[2] * m[7] - m[13] * m[3] * m[6];
    inv[6] = -m[0] * m[6] * m[15] + m[0] * m[7] * m[14] + m[4] * m[2] * m[15] - m[4] * m[3] * m[14] - m[12] * m[2] * m[7] + m[12] * m[3] * m[6];
    inv[10] = m[0] * m[5] * m[15] - m[0] * m[7] * m[13] - m[4] * m[1] * m[15] + m[4] * m[3] * m[13] + m[12] * m[1] * m[7] - m[12] * m[3] * m[5];
    inv[14] = -m[0] * m[5] * m[14] + m[0] * m[6] * m[13] + m[4] * m[1] * m[14] - m[4] * m[2] * m[13] - m[12] * m[1] * m[6] + m[12] * m[2] * m[5];
    inv[3] = -m[1] * m[6] * m[11] + m[1] * m[7] * m[10] + m[5] * m[2] * m[11] - m[5] * m[3] * m[10] - m[9] * m[2] * m[7] + m[9] * m[3] * m[6];
    inv[7] = m[0] * m[6] * m[11] - m[0] * m[7] * m[10] - m[4] * m[2] * m[11] + m[4] * m[3] * m[10] + m[8] * m[2] * m[7] - m[8] * m[3] * m[6];
    inv[11] = -m[0] * m[5] * m[11] + m[0] * m[7] * m[9] + m[4] * m[1] * m[11] - m[4] * m[3] * m[9] - m[8] * m[1] * m[7] + m[8] * m[3] * m[5];
    inv[15] = m[0] * m[5] * m[10] - m[0] * m[6] * m[9] - m[4] * m[1] * m[10] + m[4] * m[2] * m[9] + m[8] * m[1] * m[6] - m[8] * m[2] * m[5];
    var det = 1 / (m[0] * inv[0] + m[1] * inv[4] + m[2] * inv[8] + m[3] * inv[12]);
    for (var i = 0; i < 16; i++) inv[i] *= det;
    return inv;
  }
  function mulV(m, x, y, z, w) {
    return [m[0] * x + m[4] * y + m[8] * z + m[12] * w, m[1] * x + m[5] * y + m[9] * z + m[13] * w,
      m[2] * x + m[6] * y + m[10] * z + m[14] * w, m[3] * x + m[7] * y + m[11] * z + m[15] * w];
  }
  function hex(h) {
    h = h.replace('#', '');
    return [parseInt(h.substr(0, 2), 16) / 255, parseInt(h.substr(2, 2), 16) / 255, parseInt(h.substr(4, 2), 16) / 255];
  }
  function damp(a, b, lambda, dt) { return a + (b - a) * (1 - Math.exp(-lambda * dt)); }

  // CPU mirror of the swell so the ship rides the same waves as the shader
  var SW = [
    [0.944, 0.330, 0.20, 0.36, 0.62], [-0.514, 0.857, 0.13, 0.58, 0.85], [0.196, -0.981, 0.07, 1.05, 1.15],
    [0.747, 0.664, 0.045, 1.70, 1.55], [-0.894, -0.447, 0.025, 2.60, 2.00],
  ];
  function waveAt(x, z, t) {
    var h = 0, gx = 0, gz = 0;
    for (var i = 0; i < SW.length; i++) {
      var w = SW[i], ph = (w[0] * x + w[1] * z) * w[3] + t * w[4], e = Math.exp(Math.sin(ph) - 1);
      h += w[2] * (e - 0.45);
      var c = w[2] * e * Math.cos(ph) * w[3];
      gx += c * w[0]; gz += c * w[1];
    }
    return { h: h, gx: gx, gz: gz };
  }

  function rogueAt(x, z, R) {
    if (!R || R[3] <= 0) return { h: 0, gx: 0, gz: 0 };
    var u = x * R[0] + z * R[1] - R[2], perp = -x * R[1] + z * R[0];
    var kk = u > 0 ? 0.42 : 0.24, side = Math.exp(-perp * perp * 0.0022);
    var ch = 1 / Math.cosh(u * kk), s2 = ch * ch, a = R[3] * side;
    var du = a * (-2 * s2 * Math.tanh(u * kk) * kk);
    return { h: a * s2, gx: du * R[0], gz: du * R[1] };
  }
  function rippleAt(x, z, rip, t) {
    var h = 0;
    for (var i = 0; i < 8; i++) {
      var amp = rip[i * 4 + 3]; if (amp <= 0) continue;
      var age = t - rip[i * 4 + 2], dx = x - rip[i * 4], dz = z - rip[i * 4 + 1];
      var rr = Math.sqrt(dx * dx + dz * dz) + 1e-3, xx = rr - age * 2.6;
      h += Math.exp(-xx * xx * 0.35) * Math.exp(-age * 0.45) * amp / (1 + rr * 0.06) * (1 - Math.exp(-rr * rr * 0.5)) * Math.sin(xx * 2.4);
    }
    return h;
  }

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
    return s;
  }
  function program(gl, vs, fs, attr) {
    var p = gl.createProgram();
    gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, vs));
    gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, fs));
    gl.bindAttribLocation(p, 0, attr);
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p));
    var u = {}, n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
    for (var i = 0; i < n; i++) {
      var info = gl.getActiveUniform(p, i);
      u[info.name.replace(/\[0\]$/, '')] = gl.getUniformLocation(p, info.name);
    }
    return { p: p, u: u };
  }

  function OceanHero(root) {
    var canvas = root.querySelector('canvas');
    var cfg = root.dataset;
    var small = window.matchMedia('(max-width: 749px)').matches;
    var coarse = window.matchMedia('(pointer: coarse)').matches;
    var lite = small || coarse || (navigator.hardwareConcurrency || 8) <= 4;
    var calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var toggle = root.querySelector('[data-ocean-toggle]');
    var paused = false;
    try { paused = sessionStorage.getItem('ocean-hero-paused') === '1'; } catch (e) {}

    var gl = canvas.getContext('webgl', {
      antialias: false, alpha: false, depth: true, stencil: false,
      preserveDrawingBuffer: false, powerPreference: 'high-performance', desynchronized: true,
    });
    if (!gl) { root.classList.add('ocean-hero--fallback'); return; }

    var sky, sea, ship;
    try {
      var def = lite ? '#define LITE\n' : '';
      sky = program(gl, VS_SKY, def + FS_SKY, 'aPos');
      sea = program(gl, VS_SEA, def + FS_SEA, 'aGrid');
      ship = program(gl, VS_SHIP, FS_SHIP, 'aPos');
    } catch (e) {
      console.warn('[ocean-hero]', e);
      root.classList.add('ocean-hero--fallback');
      return;
    }
    gl.enableVertexAttribArray(0);

    // ---- geometry ----
    function buf(data) { var b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b); gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW); return b; }
    var quad = buf(new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]));
    var unit = buf(new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]));

    // Sea grid: dense near the camera, sparse toward the horizon (< 65k verts, 16-bit indices)
    var NX = lite ? 96 : 170, NZ = lite ? 64 : 120;
    var verts = new Float32Array((NX + 1) * (NZ + 1) * 2), k = 0, i, j;
    for (j = 0; j <= NZ; j++) {
      var tz = j / NZ, z = 12 - 232 * Math.pow(tz, 2.2);
      var span = 14 + (12 - z) * 1.15;
      for (i = 0; i <= NX; i++) { verts[k++] = (i / NX - 0.5) * 2 * span; verts[k++] = z; }
    }
    var idx = new Uint16Array(NX * NZ * 6); k = 0;
    for (j = 0; j < NZ; j++) for (i = 0; i < NX; i++) {
      var a = j * (NX + 1) + i, b = a + 1, c = a + NX + 1, d = c + 1;
      idx[k++] = a; idx[k++] = c; idx[k++] = b; idx[k++] = b; idx[k++] = c; idx[k++] = d;
    }
    var gridBuf = buf(verts);
    var idxBuf = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, idxBuf);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idx, gl.STATIC_DRAW);
    var idxCount = idx.length;

    // ---- ship texture ----
    var tex = gl.createTexture(), texReady = false, aspect = 1;
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = function () {
      aspect = img.naturalWidth / img.naturalHeight;
      var pot = document.createElement('canvas'); // power-of-two copy so we can mipmap thin rigging
      pot.width = pot.height = lite ? 1024 : 2048;
      pot.getContext('2d').drawImage(img, 0, 0, pot.width, pot.height);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, pot);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      var af = gl.getExtension('EXT_texture_filter_anisotropic');
      if (af) gl.texParameterf(gl.TEXTURE_2D, af.TEXTURE_MAX_ANISOTROPY_EXT, 4);
      texReady = true;
      root.classList.add('ocean-hero--ship-ready');
      if (!running) frame(performance.now());
    };
    if (cfg.ship) img.src = cfg.ship;

    // ---- palette & scene ----
    var P = {
      top: hex(cfg.skyTop || '#070b17'), mid: hex(cfg.skyMid || '#2a2c49'),
      hor: hex(cfg.horizon || '#d9905c'), sun: hex(cfg.sun || '#ffc47e'),
      deep: hex(cfg.deep || '#06121c'), sss: hex(cfg.sss || '#1f6f6a'),
    };
    var sunDir = (function () { var v = [0.24, 0.045, -1]; var l = Math.hypot(v[0], v[1], v[2]); return [v[0] / l, v[1] / l, v[2] / l]; })();
    var shipX = parseFloat(cfg.shipX || '2.6'), shipZ = parseFloat(cfg.shipZ || '-6.5');
    var shipH = parseFloat(cfg.shipHeight || '4.8'), water = parseFloat(cfg.waterline || '0.25');

    // ---- interaction state ----
    var ptr = { x: 0, y: 0, nx: 0, ny: 0, inside: false, moved: false };
    var look = { x: 0, y: 0 }, lean = 0, poolStrength = 0;
    var cursorW = [0, 0], lastWake = { x: 1e9, z: 1e9, t: -1 };
    var ripples = new Float32Array(32), ripI = 0; // 8 x (x, z, startTime, amplitude)
    // occasional rogue swell
    var rogueU = new Float32Array(4), rogueOn = false, rogueFront = 0, rogueH = 0, nextRogue = 5.5 + Math.random() * 1.5; // first swell ~2-3s after load
    var ROGUE_SPEED = 9, ROGUE_START = -70, ROGUE_END = 24;
    // ship dynamics (spring-damper, so it has weight and inertia)
    var hull = { y: 0, vy: 0, roll: 0, vr: 0, pitch: 0, vp: 0, init: false };
    var camHeave = 0;
    var lastDt = 1 / 60, time = 4.0, running = false, visible = true, raf = 0, last = 0;
    var cam = [0, 2.1, 8], viewProj = null, invVP = null;
    var rect = root.getBoundingClientRect();

    function updateRect() { rect = canvas.getBoundingClientRect(); }
    window.addEventListener('scroll', updateRect, { passive: true });
    window.addEventListener('resize', updateRect, { passive: true });

    function toNdc(e) {
      ptr.x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      ptr.y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      ptr.nx = ptr.x; ptr.ny = -ptr.y;
    }
    function onMove(e) {
      if (e.pointerType === 'touch' && !e.isPrimary) return;
      toNdc(e); ptr.inside = true; ptr.moved = true;
      if (!running) start();
    }
    function onLeave() { ptr.inside = false; }
    function waterHit(nx, ny) {
      if (!invVP) return null;
      var p = mulV(invVP, nx, ny, 1, 1);
      var dx = p[0] / p[3] - cam[0], dy = p[1] / p[3] - cam[1], dz = p[2] / p[3] - cam[2];
      if (dy >= -0.002) return null; // pointing at the sky
      var s = -cam[1] / dy;
      if (s > 150) return null;
      return [cam[0] + dx * s, cam[2] + dz * s];
    }
    function addRipple(x, z, amp) {
      ripples[ripI * 4] = x; ripples[ripI * 4 + 1] = z; ripples[ripI * 4 + 2] = time; ripples[ripI * 4 + 3] = amp;
      ripI = (ripI + 1) % 8;
    }
    function onTap(e) {
      if (e.target.closest('a, button, input, select, textarea')) return;
      if (paused) { paused = false; saveToggle(); setToggle(); }
      toNdc(e);
      var hit = waterHit(ptr.nx, ptr.ny);
      if (hit) addRipple(hit[0], hit[1], calm ? 0.16 : 0.27);
      start();
      if (!running) frame(performance.now());
    }
    if (!calm) {
      root.addEventListener('pointermove', onMove, { passive: true });
      root.addEventListener('pointerleave', onLeave, { passive: true });
    }
    root.addEventListener('pointerdown', onTap, { passive: true });

    // ---- sizing: fixed pixel budget + adaptive quality ----
    var cssW = canvas.clientWidth || 1, cssH = canvas.clientHeight || 1;
    var dpr = window.devicePixelRatio || 1;
    var budget = lite ? 0.75e6 : 1.9e6; // max rendered pixels
    var quality = 1, minQuality = lite ? 0.55 : 0.6;
    function targetScale() {
      var s = Math.min(dpr, lite ? 1.5 : 2, Math.sqrt(budget / (cssW * cssH)));
      return Math.max(0.5, s * quality);
    }
    function resize() {
      var s = targetScale();
      var w = Math.max(1, Math.round(cssW * s)), h = Math.max(1, Math.round(cssH * s));
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; gl.viewport(0, 0, w, h); }
    }
    if ('ResizeObserver' in window) new ResizeObserver(function (entries) {
      var r = entries[0].contentRect; cssW = r.width || 1; cssH = r.height || 1;
      updateRect(); resize(); if (!running) frame(performance.now());
    }).observe(canvas);

    // Measure how long the GPU takes (frame-to-frame gaps). Step quality down
    // quickly when slow, back up slowly when there is headroom.
    var perfN = 0, perfSum = 0, warm = 0, downs = 0;
    function adapt(ms) {
      if (ms > 120) return;
      if (warm < 20) { warm++; return; }
      perfSum += ms; perfN++;
      if (perfN < 30) return;
      var avg = perfSum / perfN; perfN = 0; perfSum = 0;
      if (avg > 20 && quality > minQuality) { quality = Math.max(minQuality, quality - 0.12); downs++; resize(); }
      else if (avg < 15.5 && quality < 1 && downs < 4) { quality = Math.min(1, quality + 0.04); resize(); }
    }

    // ---- render ----
    function bind(b) { gl.bindBuffer(gl.ARRAY_BUFFER, b); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0); }
    function setSky(u) {
      gl.uniform3fv(u.uSunDir, sunDir);
      gl.uniform3fv(u.uSkyTop, P.top); gl.uniform3fv(u.uSkyMid, P.mid);
      gl.uniform3fv(u.uHorizon, P.hor); gl.uniform3fv(u.uSunCol, P.sun);
    }

    function update(dt) {
      var tx = ptr.inside ? ptr.x : 0, ty = ptr.inside ? ptr.y : 0;
      look.x = damp(look.x, tx, 3.2, dt); look.y = damp(look.y, ty, 3.2, dt);
      lean = damp(lean, tx, 1.8, dt);
      poolStrength = damp(poolStrength, ptr.inside && !lite ? 1 : 0, 4, dt);
      // rogue swell: launch every ~18-32s, roll it toward the viewer, then fade
      if (!calm && !paused) {
        if (!rogueOn && time > nextRogue) {
          rogueOn = true; rogueFront = ROGUE_START;
          var ang = (Math.random() - 0.5) * 0.5; // slight random heading
          rogueU[0] = Math.sin(ang); rogueU[1] = Math.cos(ang);
          rogueH = 1.25 + Math.random() * 0.45;
        }
        if (rogueOn) {
          rogueFront += ROGUE_SPEED * dt;
          var env = Math.min(1, Math.max(0, (rogueFront - ROGUE_START) / 28)) * (1 - Math.min(1, Math.max(0, (rogueFront - 4) / (ROGUE_END - 4))));
          env = env * env * (3 - 2 * env);
          rogueU[2] = rogueFront; rogueU[3] = rogueH * env;
          if (rogueFront > ROGUE_END) { rogueOn = false; rogueU[3] = 0; nextRogue = time + 14 + Math.random() * 10; }
        }
      }
      // retire faded ripples
      for (var r = 0; r < 8; r++) if (ripples[r * 4 + 3] > 0 && time - ripples[r * 4 + 2] > 10) ripples[r * 4 + 3] = 0;
    }

    function draw() {
      var w = canvas.width, h = canvas.height, asp = w / h;
      var portrait = asp < 1;
      var drift = calm ? 0 : Math.sin(time * 0.13) * 0.3;
      var bob = calm ? 0 : Math.sin(time * 0.41) * 0.06;
      // the viewer floats too: a gentle lift as the rogue swell passes underneath
      camHeave = damp(camHeave, rogueAt(0, portrait ? 11 : 8, rogueU).h * 0.45, 3, 1 / 60);
      cam = [look.x * 1.35 + drift, 2.1 - look.y * 0.55 + bob + camHeave + (portrait ? 0.4 : 0), portrait ? 11 : 8];
      var centre = [look.x * 0.55 + (portrait ? 0 : 0.6), (portrait ? -4.4 : 1.3) - look.y * 0.25, -20];
      var fov = portrait ? 0.95 : 0.72;
      viewProj = mul(perspective(fov, asp, 0.1, 400), lookAt(cam, centre, [0, 1, 0]));
      invVP = invert(viewProj);

      // cursor on the water: light pool + wake trail
      var hit = ptr.inside ? waterHit(ptr.nx, ptr.ny) : null;
      if (hit) {
        cursorW[0] = damp(cursorW[0], hit[0], 10, 1 / 60); cursorW[1] = damp(cursorW[1], hit[1], 10, 1 / 60);
        if (ptr.moved && !calm) {
          var dx = hit[0] - lastWake.x, dz = hit[1] - lastWake.z;
          if (dx * dx + dz * dz > 1.4 && time - lastWake.t > 0.14) {
            addRipple(hit[0], hit[1], 0.07);
            lastWake.x = hit[0]; lastWake.z = hit[1]; lastWake.t = time;
          }
        }
      }
      ptr.moved = false;

      // sky
      gl.disable(gl.BLEND); gl.disable(gl.DEPTH_TEST); gl.depthMask(false);
      gl.useProgram(sky.p);
      gl.uniformMatrix4fv(sky.u.uInvViewProj, false, invVP);
      gl.uniform3fv(sky.u.uCamPos, cam);
      gl.uniform1f(sky.u.uTime, time);
      setSky(sky.u);
      bind(quad);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

      // sea
      gl.enable(gl.DEPTH_TEST); gl.depthMask(true); gl.clear(gl.DEPTH_BUFFER_BIT);
      gl.useProgram(sea.p);
      gl.uniformMatrix4fv(sea.u.uViewProj, false, viewProj);
      gl.uniform3fv(sea.u.uCamPos, cam);
      gl.uniform1f(sea.u.uTime, time);
      gl.uniform3fv(sea.u.uDeep, P.deep);
      gl.uniform3fv(sea.u.uSSS, P.sss);
      gl.uniform1f(sea.u.uFogDist, 190);
      gl.uniform4fv(sea.u.uRip, ripples);
      gl.uniform4fv(sea.u.uRogue, rogueU);
      gl.uniform3f(sea.u.uCursor, cursorW[0], poolStrength, cursorW[1]);
      setSky(sea.u);
      bind(gridBuf);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, idxBuf);
      gl.drawElements(gl.TRIANGLES, idxCount, gl.UNSIGNED_SHORT, 0);

      if (!texReady) return;
      // ship rides the swell and leans a little toward the cursor
      var sx = portrait ? 0 : shipX, sz = portrait ? shipZ - 2.5 : shipZ;
      // sample the surface under bow, stern, and either side of the hull
      var L = shipH * aspect * 0.34, B = 0.9;
      function surf(x, z) { return waveAt(x, z, time).h + rogueAt(x, z, rogueU).h + rippleAt(x, z, ripples, time); }
      var hBow = surf(sx - L, sz), hSt = surf(sx + L, sz), hP = surf(sx, sz - B), hS = surf(sx, sz + B), hC = surf(sx, sz);
      var tY = (hBow + hSt + hP + hS + hC * 2) / 6 * 0.9 - 0.02;
      var tRoll = Math.atan2(hSt - hBow, 2 * L) + lean * 0.035;
      var tPitch = Math.atan2(hS - hP, 2 * B);
      var fdt = Math.min(0.05, lastDt);
      if (!hull.init) { hull.y = tY; hull.roll = tRoll; hull.pitch = tPitch; hull.init = true; }
      hull.vy += ((tY - hull.y) * 16 - hull.vy * 4.2) * fdt; hull.y += hull.vy * fdt;
      hull.vr += ((tRoll - hull.roll) * 13 - hull.vr * 3.6) * fdt; hull.roll += hull.vr * fdt;
      hull.vp += ((tPitch - hull.pitch) * 13 - hull.vp * 3.6) * fdt; hull.pitch += hull.vp * fdt;
      hull.roll = Math.max(-0.3, Math.min(0.3, hull.roll)); // never capsizes
      hull.pitch = Math.max(-0.6, Math.min(0.6, hull.pitch));
      var pos = [sx, hull.y, sz];
      var roll = hull.roll, pitch = hull.pitch;

      gl.useProgram(ship.p);
      gl.uniformMatrix4fv(ship.u.uViewProj, false, viewProj);
      gl.uniform3fv(ship.u.uShipPos, pos);
      gl.uniform2f(ship.u.uSize, shipH * aspect, shipH);
      gl.uniform1f(ship.u.uRoll, -roll);
      gl.uniform1f(ship.u.uPitch, pitch);
      gl.uniform1f(ship.u.uWater, water);
      gl.uniform1f(ship.u.uTime, time);
      gl.uniform1f(ship.u.uSeaY, pos[1]);
      gl.uniform3f(ship.u.uTint, 0.8, 0.74, 0.71);
      gl.uniform3fv(ship.u.uSunCol, P.sun);
      gl.uniform1f(ship.u.uRim, 1);
      // a steep swell under the hull breaks up the mirror image
      gl.uniform1f(ship.u.uRefl, Math.max(0, 1 - rogueAt(sx, sz, rogueU).h * 1.4 - Math.abs(hull.vy) * 0.6));
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.uniform1i(ship.u.uTex, 0);
      bind(unit);
      gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.disable(gl.DEPTH_TEST); // reflection first, under the hull
      gl.uniform1f(ship.u.uMirror, 1);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      gl.enable(gl.DEPTH_TEST); gl.depthMask(false); // hull: sea in front hides the waterline
      gl.uniform1f(ship.u.uMirror, 0);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }

    function frame(now) {
      raf = 0;
      // cap at ~60fps on 120/144Hz screens
      if (running && last && now - last < 14) { raf = requestAnimationFrame(frame); return; }
      var ms = last ? now - last : 16;
      var dt = Math.min(ms / 1000, 0.05);
      last = now;
      if (!paused) time += dt * (calm ? 0.5 : 1);
      lastDt = paused ? 0 : dt * (calm ? 0.5 : 1);
      update(dt);
      draw();
      root.classList.add('ocean-hero--live');
      if (running) { adapt(ms); raf = requestAnimationFrame(frame); }
    }
    function start() {
      if (running || paused || !visible || document.hidden) return;
      running = true; last = 0; raf = requestAnimationFrame(frame);
    }
    function stop() { running = false; if (raf) cancelAnimationFrame(raf); raf = 0; }

    new IntersectionObserver(function (e) {
      visible = e[0].isIntersecting;
      if (visible) { updateRect(); start(); } else stop();
    }, { rootMargin: '0px 0px -10% 0px' }).observe(root);
    document.addEventListener('visibilitychange', function () { document.hidden ? stop() : start(); });
    canvas.addEventListener('webglcontextlost', function (e) { e.preventDefault(); stop(); root.classList.add('ocean-hero--fallback'); });

    function saveToggle() { try { sessionStorage.setItem('ocean-hero-paused', paused ? '1' : '0'); } catch (e) {} }
    function setToggle() {
      if (!toggle) return;
      toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
      toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
      root.classList.toggle('ocean-hero--paused', paused);
    }
    if (toggle) {
      toggle.hidden = false;
      toggle.addEventListener('click', function () {
        paused = !paused; saveToggle(); setToggle();
        paused ? stop() : start();
      });
    }
    setToggle();
    // lets the theme editor / QA trigger a swell on demand: el.__oceanRogue()
    root.__oceanRogue = function (front) {
      if (!rogueOn) nextRogue = time;
      if (typeof front === 'number') { update(0); rogueFront = front; hull.init = false; }
      start();
    };

    resize();
    frame(performance.now());
    start();
  }

  function boot() {
    document.querySelectorAll('[data-ocean-hero]').forEach(function (el) {
      if (el.__ocean) return; el.__ocean = true;
      try { OceanHero(el); } catch (e) { console.warn('[ocean-hero]', e); el.classList.add('ocean-hero--fallback'); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
